import { useState } from 'react'
import {
  useCreateContentVersionAnnotation,
  type AnnotationLayout,
  type ContentVersionAnnotation,
} from '@opallabs/api'
import { Spacing } from '@opallabs/gem'
import { useRelativeTimestamp } from '@opallabs/hooks'
import {
  Avatar,
  Button,
  ButtonSize,
  Icon,
  Popover,
  Tooltip,
} from '@opallabs/lapidary'
import { MentionComposer } from './MentionComposer'
import { isInsideMentionSuggestions } from './mentionSuggestionRenderer'
import { MentionText } from './mentionText'
import * as styles from './styles'

/** Mirrors +ContentVersionAnnotations::CreateResource::MAX_COMMENT_LENGTH+. */
const COMMENT_MAX_LENGTH = 2000

// A component so the hook can rerun per pin; the overlay maps over annotations.
const PinTimestamp: React.VFC<{ createdAt: string }> = ({ createdAt }) => {
  const displayedTimestamp = useRelativeTimestamp(createdAt, navigator.language)
  return <span css={styles.pinPopoverTime}>{displayedTimestamp}</span>
}

type AnnotationOverlayProps = {
  /** The version whose pin annotations this overlay owns; needed to create new ones. */
  versionId: string
  /** The coordinate space of the layout underneath, recorded on new pins. */
  layout: AnnotationLayout
  annotations: ContentVersionAnnotation[]
  draft: { x: number; y: number } | null
  /** Comment mode: whether clicks on the layer stage a draft pin. */
  armed: boolean
  hoveredId: string | null
  /** The pin whose comment popover is open, or null. */
  openId: string | null
  onHoverChange: (id: string | null) => void
  onPlaceDraft: (position: { x: number; y: number }) => void
  onOpenChange: (id: string | null) => void
  onDismissDraft: () => void
  /** Resolving takes the pin off the canvas; the draft composer has no such action. */
  onResolve: (id: string) => void
}

/**
 * Pin layer over the version preview. It captures clicks to stage a draft pin at
 * fractional coordinates and renders each annotation as a numbered pin. Clicking a
 * pin opens a popover with its comment; a staged draft opens a popover with the
 * create composer. Rendered inside VersionPreview's positioned frame, so pins scale
 * with the preview and the popovers anchor to the pin elements.
 */
export const AnnotationOverlay: React.VFC<AnnotationOverlayProps> = ({
  versionId,
  layout,
  annotations,
  draft,
  armed,
  hoveredId,
  openId,
  onHoverChange,
  onPlaceDraft,
  onOpenChange,
  onDismissDraft,
  onResolve,
}) => (
  // Disarmed, the layer lets clicks through to the content (carousel arrows
  // etc.) — only the pins stay interactive. Armed, it captures clicks to
  // stage a draft pin.
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the click's coordinates are the input, so there is no keyboard equivalent, and the pin buttons inside rule out making the layer a control
  <div
    css={styles.annotationOverlay(armed)}
    onClick={
      armed
        ? (event) => {
            // Popover content is portaled to the body, but React still bubbles
            // its events through this tree. Without this guard a click inside an
            // open popover (the composer's textarea, Cancel, Save) re-places the
            // draft: the composer stays mounted holding the text the user just
            // dismissed, now anchored to a pin somewhere else entirely. Only
            // clicks that land on the layer itself stage a pin — every child
            // stops its own propagation.
            if (event.target !== event.currentTarget) return
            const rect = event.currentTarget.getBoundingClientRect()
            onPlaceDraft({
              x: (event.clientX - rect.left) / rect.width,
              y: (event.clientY - rect.top) / rect.height,
            })
          }
        : undefined
    }
  >
    {annotations.map((annotation, index) => (
      <Popover.Root
        key={annotation.id}
        open={openId === annotation.id}
        onOpenChange={(open) => onOpenChange(open ? annotation.id : null)}
      >
        <Popover.Trigger>
          <button
            type="button"
            aria-label={`Feedback ${index + 1}`}
            // Stays enlarged while its popover is open: the popover anchors to
            // this element, and a size change would shift the open popover.
            css={styles.annotationPin(
              hoveredId === annotation.id || openId === annotation.id,
              armed
            )}
            style={{
              insetInlineStart: `${annotation.x * 100}%`,
              insetBlockStart: `${annotation.y * 100}%`,
            }}
            onClick={(event) => event.stopPropagation()}
            onMouseEnter={() => onHoverChange(annotation.id)}
            onMouseLeave={() => onHoverChange(null)}
          >
            {index + 1}
          </button>
        </Popover.Trigger>
        <Popover.Content
          side="right"
          align="start"
          sideOffset={8}
          zIndex={styles.popoverZIndex}
          // This popover is a comment to read, not a form to fill in. Radix
          // would otherwise focus its first focusable element — the resolve
          // action — which arrives focus-ringed and, since the tooltip shows on
          // focus, already labelled, as though it were being proposed. Focus
          // stays on the pin; Tab still walks into the popover.
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div css={styles.pinPopover}>
            <div css={styles.pinPopoverHeader}>
              <Avatar fullName={annotation.authorName} size={Spacing.L} />
              <span css={styles.pinPopoverName}>{annotation.authorName}</span>
              <PinTimestamp createdAt={annotation.createdAt} />
              {/* Where a document comment keeps its own actions: compact, in the
                  header, named by a tooltip rather than a label. */}
              <Tooltip
                content="Resolve Feedback"
                zIndex={styles.abovePopoverZIndex}
              >
                <Button
                  variant="ghost"
                  name="resolve feedback"
                  ariaLabel="Resolve Feedback"
                  icon={<Icon glyph="gem-check-circle" />}
                  size={ButtonSize.Small}
                  css={styles.pinPopoverAction}
                  onClick={() => onResolve(annotation.id)}
                />
              </Tooltip>
            </div>
            <p css={styles.pinPopoverComment}>
              <MentionText text={annotation.comment} />
            </p>
          </div>
        </Popover.Content>
      </Popover.Root>
    ))}
    {draft && (
      <Popover.Root
        open
        onOpenChange={(open) => {
          if (!open) onDismissDraft()
        }}
      >
        <Popover.Anchor asChild>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- a marker for the popover to anchor to, not a control: the composer is already open, and the handler only keeps the click off the layer, which would re-place the pin under it */}
          <div
            css={styles.annotationDraftPin}
            style={{
              insetInlineStart: `${draft.x * 100}%`,
              insetBlockStart: `${draft.y * 100}%`,
            }}
            onClick={(event) => event.stopPropagation()}
          />
        </Popover.Anchor>
        <Popover.Content
          side="right"
          align="start"
          sideOffset={8}
          zIndex={styles.popoverZIndex}
          // The mention list is portaled to the body, a click away from this
          // popover. Its own nested Radix layer keeps that from counting as an
          // outside interaction today; this stands in if the shared list ever
          // stops wrapping itself in one, because the cost of being wrong is the
          // draft pin and the comment being written against it.
          onInteractOutside={(event) => {
            if (isInsideMentionSuggestions(event.target)) event.preventDefault()
          }}
        >
          <DraftComposer
            versionId={versionId}
            layout={layout}
            draft={draft}
            onDismiss={onDismissDraft}
          />
        </Popover.Content>
      </Popover.Root>
    )}
  </div>
)

type DraftComposerProps = {
  versionId: string
  layout: AnnotationLayout
  draft: { x: number; y: number }
  onDismiss: () => void
}

const DraftComposer: React.VFC<DraftComposerProps> = ({
  versionId,
  layout,
  draft,
  onDismiss,
}) => {
  const [comment, setComment] = useState('')
  const { mutate: createAnnotation, isLoading } =
    useCreateContentVersionAnnotation(versionId)

  const trimmed = comment.trim()
  // Shared by the Save button and the keyboard shortcut, so the two can't
  // disagree about when a draft may be posted.
  const canSave =
    trimmed.length > 0 && trimmed.length <= COMMENT_MAX_LENGTH && !isLoading

  const save = () => {
    if (!canSave) return
    createAnnotation(
      { x: draft.x, y: draft.y, layout, comment: trimmed },
      { onSuccess: onDismiss }
    )
  }

  return (
    <div css={styles.draftPopover}>
      <MentionComposer onChange={setComment} onSubmit={save} />
      <div css={styles.composerActions}>
        <Button
          buttonText="Cancel"
          variant="tertiary"
          name="Cancel feedback"
          onClick={onDismiss}
        />
        <Button
          buttonText="Save"
          variant="secondary"
          name="Save feedback"
          disabled={!canSave}
          onClick={save}
        />
      </div>
    </div>
  )
}
